import emojiRegex from 'emoji-regex';
import { debounce, findIndex, flatten, identity, noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import detectOutsideClick from 'react-click-outside';
import styled, { css } from 'styled-components';
import querystring from 'querystring';

import { INTERACTION_TYPES, EVENT_INITIATORS } from '@zocdoc/frontend-metrics';
import { grey25, navy, white } from '@zocdoc/branding/colors';

import { breakpoint } from '../grid';
import { withEvents } from '../mixins';
import { FontFamily } from '../typography';
import * as AppEvents from './constants/AppEvents';
import * as LoggingConstants from './constants/LoggingConstants';
import stringifyIdsInAPIResponse from './utils/stringifyIdsInAPIResponse';
import SearchDropdown from './views/searchDropdown';
import SearchTextBox from './views/searchTextBox';

const MINIMUM_STRING_SIZE = 3;
const TEXT_LENGTH_LIMIT = 256;

export class UniversalSearchBase extends Component {
    static propTypes = {
        defaultValue: PropTypes.string,
        carrierId: PropTypes.string,
        className: PropTypes.string,
        directoryId: PropTypes.string, // Placeholder. Needs to be updated with actual value when available
        gaCategory: PropTypes.string,
        isMobile: PropTypes.bool,
        latitude: PropTypes.number.isRequired,
        locationEl: PropTypes.object,
        longitude: PropTypes.number.isRequired,
        monolithTrigger: PropTypes.shape({
            trigger: PropTypes.func,
        }),
        onChangeValue: PropTypes.func,
        onDropdownItemClick: PropTypes.func,
        onResultFetched: PropTypes.func,
        onSelectOption: PropTypes.func,
        onTextBoxChange: PropTypes.func,
        onTextBoxFocus: PropTypes.func,
        pageId: PropTypes.string,
        placeholderText: PropTypes.string,
        planId: PropTypes.string,
        referrerPageType: PropTypes.string.isRequired,
        searchApiUrl: PropTypes.string.isRequired,
        searchApiV2Url: PropTypes.string,
        searchQueryGuidGetter: PropTypes.func,
        sendMetric: PropTypes.func,
        sessionId: PropTypes.string,
        theme: PropTypes.object,
        trackingId: PropTypes.string,
    };

    static defaultProps = {
        onChangeValue: noop,
        onDropdownItemClick: noop,
        onResultFetched: noop,
        onSelectOption: noop,
        onTextBoxChange: noop,
        onTextBoxFocus: noop,
        placeholderText: 'condition, procedure, doctor name...',
        sendMetric: noop,
    };

    fetchRequestCounter = 0;

    state = {
        fetchResponseCounter: 0,
    };

    constructor(props) {
        super(props);
        this.debouncedSendMetric = debounce(
            this.props.sendMetric,
            LoggingConstants.SEND_METRIC_DEBOUNCE_WAIT_TIME
        ).bind(this);
    }

    componentDidMount() {
        const {
            defaultValue,
            enableBrowseSpecialty,
            inputString,
        } = this.props;

        this.previousSelection = {
            name: inputString,
        };

        document.addEventListener('keyup', this.onKeyUp);

        if (document.readyState === 'complete') {
            this.initializeUniversalSearch();
        } else {
            window.addEventListener('load', this.initializeUniversalSearch);
        }

        if (defaultValue) {
            this.handleInputChange(defaultValue);
        }

        enableBrowseSpecialty();
    }

    componentWillUnmount() {
        document.removeEventListener('keyup', this.onKeyUp);
        window.removeEventListener('load', this.initializeUniversalSearch);
    }

    render() {
        const {
            className,
            gaCategory,
            inputString,
            placeholderText,
            textBoxFocused,
            transparent,
        } = this.props;

        return (
            // eslint-disable-next-line zocdoc/no-class-name
            <Container
                active={textBoxFocused}
                className={className}
                data-dropdown-active={String(textBoxFocused)}
                data-test="universal-search"
                data-uem-id="universal-search-desktop"
                transparent={transparent}
            >
                <SearchTextBox
                    clearTextHandler={this.clearText}
                    data-test="universal-search-text-box"
                    gaCategory={gaCategory}
                    inputCallbackRef={node => { this.searchInput = node; }}
                    onChange={this.handleTextBoxChange}
                    onFocus={this.handleTextBoxFocus}
                    onKeyDown={this.handleKeyDown}
                    placeholder={placeholderText}
                    textBoxFocused={textBoxFocused}
                    value={this.focusedItemText() || inputString}
                />
                {textBoxFocused && this.renderCancelButton()}
                {this.maybeRenderAutoCompleteBox()}
            </Container>
        );
    }

    maybeRenderAutoCompleteBox() {
        if (this.shouldRenderAutoComplete()) {
            return (
                <SearchDropdown
                    clickFirstSectionItemHandler={this.handleClickFirstSectionItem}
                    clickSecondSectionItemHandler={this.handleClickSecondSectionItem}
                    clickThirdSectionItemHandler={this.handleClickThirdSectionItem}
                    data-test="universal-search-dropdown"
                    firstSection={this.props.firstSection}
                    focusIndex={this.props.focusIndex}
                    isSpecialtyBrowse={this.props.currentSearchQuery.length === 0}
                    mouseMoveHandler={this.handleMouseMove}
                    onMouseLeave={this.handleMouseLeave}
                    secondSection={this.props.secondSection}
                    thirdSection={this.props.thirdSection}
                    touchStartHandler={this.handleTouchStart}
                />
            );
        }
    }

    renderCancelButton() {
        return (
            <CancelButtonContainer>
                <CancelButtonDivider />
                <CancelButton
                    onClick={this.revertChangesAndCollapseDropdown}
                    role="button"
                    tabIndex="0"
                >
                    Cancel
                </CancelButton>
            </CancelButtonContainer>
        );
    }

    shouldRenderAutoComplete() {
        return this.props.textBoxFocused
               && (this.props.firstSection.length
                   + this.props.secondSection.length
                   + this.props.thirdSection.length) > 0;
    }

    getSharedRequestParameters = () => {
        const {
            directoryId,
            isMobile,
            pageId,
            referrerPageType,
            searchQueryGuidGetter,
            sessionId,
            theme,
            trackingId,
        } = this.props;

        const params = {
            directoryId: theme ? theme.directoryId : directoryId,
            isMobile,
            pageId,
            referrerPageType,
            searchQueryGuid: searchQueryGuidGetter ? searchQueryGuidGetter() : '',
            searchQuerySequence: this.fetchRequestCounter,
            separateSemanticIndex: 1, // Adding this for backwards compatibility with logging
            sessionId,
            trackingId,
        };

        // this is handled separately so if it is falsy we don't parse it to NaN
        if (params.directoryId) {
            params.directoryId = parseInt(params.directoryId, 10);
        }

        return params;
    };

    getLocation = () => {
        const {
            locationEl,
        } = this.props;

        let { latitude, longitude } = this.props;

        if (locationEl) {
            latitude = parseFloat(locationEl.getAttribute('latitude')) || latitude;
            longitude = parseFloat(locationEl.getAttribute('longitude')) || longitude;
        }

        return {
            latitude,
            longitude,
            distance: 50,
        };
    };

    initializeUniversalSearch = () => {
        this.postSearchApi(LoggingConstants.pageLoadEvent, '');
        this.props.populateBrowseSpecialties();
    };

    postSearchApi = (eventType, searchString, selectedItem) => {
        const parameters = {
            eventType,
            location: this.getLocation(),
            name: searchString,
            size: eventType === LoggingConstants.autocompleteEvent ? 3 : 0,
            ...this.getSharedRequestParameters(),
        };

        if (selectedItem) {
            parameters.selectedItem = Object.assign({}, selectedItem, {
                // autocomplete expects a numerical ID to be sent
                id: parseInt(selectedItem.id, 10),
            });
        }

        return fetch(this.props.searchApiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(parameters),
        }).catch(e => console.error(e)); // eslint-disable-line no-console
    };

    getSearchApi = searchString => {
        // used with new data format
        const queryData = {
            searchQuery: searchString,
            size: 8, // Overriding this value in search service if trimResults experiment is turned ON
            ...this.getLocation(),
            ...this.getSharedRequestParameters(),
        };

        const queryStringSection = querystring.stringify(queryData);
        const fullUrl = `${this.props.searchApiV2Url}?${queryStringSection}`;

        // eslint-disable-next-line no-console
        return fetch(fullUrl).catch(e => console.error(e));
    };

    matchesInputString = item => {
        const a = this.itemText(item);
        const b = this.props.inputString;
        return a && b && (a.trim().toLowerCase() === b.trim().toLowerCase());
    };

    isMostPopular(item) {
        return item.mostPopular;
    }

    handleKeyDown = event => {
        const totalItemCount = this.props.firstSection.length
            + this.props.secondSection.length
            + this.props.thirdSection.length;

        let currentIndex = this.props.focusIndex;

        if (event.key === 'ArrowUp') {
            if (currentIndex > -1) {
                this.props.previousOption();
            }
            event.preventDefault();
        } else if (event.key === 'ArrowDown') {
            if (currentIndex < (totalItemCount - 1)) {
                this.props.nextOption();
            }
            event.preventDefault();
        } else if (event.key === 'Enter') {
            if (this.shouldRenderAutoComplete() && this.props.focusIndex > -1) {
                // the enter keypress should not be allowed to propagate into a submit cascade when
                // the dropdown is visible because the new value will not have been applied yet to the
                // input box; a second enter press after the dropdown is closed is A-OK to submit
                // (same behavior as the Insurance Picker )

                event.stopPropagation();
                event.preventDefault();
            }

            currentIndex = currentIndex !== -1 ? currentIndex : this.getFoundIndex();

            if (currentIndex === -1) {
                if (this.props.monolithTrigger) {
                    this.props.monolithTrigger.trigger(AppEvents.ENTER_PRESSED);
                }
            } else if (currentIndex < this.props.firstSection.length) {
                this.handleSelectFirstSectionItem(currentIndex);
            } else if (currentIndex < (this.props.firstSection.length + this.props.secondSection.length)) {
                this.handleSelectSecondSectionItem(currentIndex);
            } else {
                this.handleSelectThirdSectionItem(currentIndex);
            }
        } else if (event.key === 'Tab') {
            this.selectMostPopularItemThenCollapseDropdown();
        }
    };

    getFoundIndex = () => {
        const all = this.allItems();
        let foundIndex = findIndex(all, this.matchesInputString);
        if (foundIndex === -1) {
            foundIndex = findIndex(all, this.isMostPopular);
        }

        if (foundIndex > -1) {
            return foundIndex;
        } else if (all.length > 0) {
            return 0;
        } else {
            return -1;
        }
    };

    allItems = () => {
        const sections = [
            this.props.firstSection,
            this.props.secondSection,
            this.props.thirdSection,
        ].filter(identity);

        return flatten(sections);
    };

    itemText = item => item ? item.fullName || item.name : null;

    focusedItemText = () => {
        if (this.props.focusIndex === -1) {
            return null;
        }
        const focusedItem = this.allItems()[this.props.focusIndex];
        return this.itemText(focusedItem);
    };

    createSectionClickHandler = cb => e => {
        const abortProxyClick = cb(parseInt(e.currentTarget.getAttribute('data-index'), 10));

        if (!abortProxyClick) {
            this.props.onDropdownItemClick(e);
        }
    };

    handleInputChange = value => {
        this.props.setInputString(value);
        this.debouncedSendMetric({
            action: 'Change Doctor/Practice Name',
            element: LoggingConstants.METRICS_CONSTANTS.Textbox,
            eventDetails: {
                Query: value,
            },
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.TYPE,
        });
        this.props.onChangeValue(value);
    }

    handleSelectFirstSectionItem = index => {
        const specialtyProcedure = this.props.firstSection[index];

        this.handleSelectSpecialtyProcedure(
            specialtyProcedure,
            index
        );
    };

    handleSelectSecondSectionItem = index => {
        const secondSectionIndex = index - this.props.firstSection.length;
        const specialtyProcedure = this.props.secondSection[secondSectionIndex];

        this.handleSelectSpecialtyProcedure(
            specialtyProcedure,
            index
        );
    };

    handleSelectThirdSectionItem = index => {
        const doctorIndex = index - this.props.firstSection.length - this.props.secondSection.length;
        const doctorId = this.props.thirdSection[doctorIndex].id;
        const { profileUrl } = this.props.thirdSection[doctorIndex]; // Available on search-service payload

        const doctorName = this.props.thirdSection[doctorIndex].fullName;
        const relativePosition = this.calculateRelativePosition(index);
        const position = index + 1;

        const selectedItem = {
            id: doctorId,
            name: doctorName,
            position,
            relativePosition,
            type: LoggingConstants.providerType,
            userInput: this.props.inputString,
        };

        const eventDetails = {
            position,
            relativePosition,
            selection: doctorName,
            selectionType: LoggingConstants.providerType,
        };

        this.sendDropdownItemSelectMetric(eventDetails);

        this.postSearchApi(LoggingConstants.userSelectionEvent, '', selectedItem);

        if (this.props.monolithTrigger) {
            this.props.monolithTrigger.trigger(AppEvents.OPEN_PROFILE, profileUrl);
        } else {
            window.location.assign(profileUrl);

            // this tells the click handler not to bother proxying the click up since we're switching pages
            return true;
        }
    };

    handleClickFirstSectionItem = this.createSectionClickHandler(this.handleSelectFirstSectionItem);
    handleClickSecondSectionItem = this.createSectionClickHandler(this.handleSelectSecondSectionItem);
    handleClickThirdSectionItem = this.createSectionClickHandler(this.handleSelectThirdSectionItem);

    handleSelectSpecialtyProcedure = (specialtyProcedure, index) => {
        const specialtyProcedureInfo = this.createSpecialtyProcedureInfo(specialtyProcedure);
        let id;
        let type;

        if (!specialtyProcedure.dataSource || specialtyProcedure.dataSource.includes('procedure')) { // v1, v2 procedure
            id = specialtyProcedureInfo.procedureId;
            type = LoggingConstants.procedureType;
        } else if (specialtyProcedure.dataSource.includes('semantic')) { // v1 or v2 semantic
            id = specialtyProcedureInfo.specialtyId;
            type = LoggingConstants.semanticType;
        } else if (specialtyProcedure.dataSource === LoggingConstants.browseSpecialtyType) { // specialty browse
            id = specialtyProcedureInfo.specialtyId;
            type = LoggingConstants.browseSpecialtyType;
        } else {
            id = specialtyProcedureInfo.specialtyId;
            type = LoggingConstants.specialtyType;
        }

        // log which autosuggest result user select
        const relativePosition = this.calculateRelativePosition(index);
        const position = index + 1;

        const selectedItem = {
            focusIndex: this.props.focusIndex,
            id,
            mostPopular: specialtyProcedure.mostPopular,
            name: specialtyProcedureInfo.name,
            position,
            relativePosition,
            type,
            userInput: this.props.inputString,
        };

        this.postSearchApi(LoggingConstants.userSelectionEvent, '', selectedItem);

        const eventDetails = {
            position,
            relativePosition,
            selection: specialtyProcedureInfo.name,
            selectionType: type,
        };

        this.sendDropdownItemSelectMetric(eventDetails);

        if (this.props.monolithTrigger) {
            this.props.monolithTrigger.trigger(AppEvents.SEARCH_BY_SPECIALTY_PROCEDURE, specialtyProcedureInfo);
        }

        this.props.onSelectOption(specialtyProcedureInfo);

        this.previousSelection = specialtyProcedureInfo;
        if (this.props.focusIndex > -1) {
            this.handleInputChange(specialtyProcedure.name);
        }
        this.collapseDropdown();
    };

    getDefaultSpecialtyProcedure() {
        if (this.props.firstSection.length > 0) {
            return this.createSpecialtyProcedureInfo(
                this.props.firstSection[0]
            );
        } else if (this.props.secondSection.length > 0) {
            return this.createSpecialtyProcedureInfo(
                this.props.secondSection[0]
            );
        }

        return null;
    }

    createSpecialtyProcedureInfo(specialtyProcedure) {
        const {
            name,
            procedureId,
            procedureName,
            specialtyId,
            specialtyInsuranceTypeId,
        } = specialtyProcedure;

        return {
            name,
            procedureId,
            procedureName,
            specialtyId,
            specialtyInsuranceTypeId,
        };
    }

    selectMostPopularItemThenCollapseDropdown = () => {
        // short circuit if the dropdown isn't even rendered
        if (!this.shouldRenderAutoComplete()) { return; }

        if (!this.props.textBoxFocused || this.props.inputString.length < MINIMUM_STRING_SIZE) {
            return this.collapseDropdown();
        }

        const foundIndex = this.getFoundIndex();
        if (foundIndex === -1) {
            this.collapseDropdown();
        } else if (foundIndex < this.props.firstSection.length) {
            this.handleSelectFirstSectionItem(foundIndex);
        } else if (foundIndex < (this.props.firstSection.length + this.props.secondSection.length)) {
            this.handleSelectSecondSectionItem(foundIndex);
        } else {
            this.previousSelection = {
                name: this.props.inputString,
            };

            this.collapseDropdown();
        }
    };

    revertChangesAndCollapseDropdown = () => {
        this.handleInputChange(this.previousSelection.name);
        this.collapseDropdown();
    };

    collapseDropdown() {
        this.props.setCurrentSearchQuery('');
        this.props.setTextBoxFocused(false);
        this.props.setFocusIndex(-1);
        this.props.setFirstSection([]);
        this.props.setSecondSection([]);
        this.props.setThirdSection([]);

        if (this.props.isMobile) {
            this.searchInput.blur();
        }
    }

    clearText = () => {
        this.fetchRequestCounter += 1;

        this.setState({
            fetchResponseCounter: this.fetchRequestCounter,
        });

        this.handleInputChange('');
        this.props.setCurrentSearchQuery('');
        this.props.setFirstSection([]);
        this.props.setSecondSection([]);
        this.props.setThirdSection([]);
        this.props.setFocusIndex(-1);
        this.searchInput.focus();

        this.props.populateBrowseSpecialties();
    };

    handleMouseMove = e => {
        const index = parseInt(e.currentTarget.getAttribute('data-index'), 10);

        if (index !== this.props.focusIndex) {
            this.props.setFocusIndex(index);
        }
    };

    // this handler exists to fix buggy behavior on iOS where mouseOver event
    // prevents click event from being fired before user has entered input.
    // on browsers that respond to touch events, use in place of mouse over
    handleTouchStart = e => {
        this.handleMouseMove(e);
        e.nativeEvent.stopImmediatePropagation();
    }

    handleMouseLeave = () => this.props.setFocusIndex(-1);

    handleTextBoxChange = event => {
        if (event.target.value.length > TEXT_LENGTH_LIMIT) {
            event.target.value = event.target.value.substring(0, TEXT_LENGTH_LIMIT);
        }

        this.handleInputChange(event.target.value);
        this.props.setTextBoxFocused(true);

        event.persist();

        if (this.props.monolithTrigger) {
            this.props.monolithTrigger.trigger(AppEvents.TEXTBOX_CHANGE, event);
        } else {
            this.props.onTextBoxChange(event);
        }

        this.fetchRequestCounter += 1;
        event.fetchRequestCounter = this.fetchRequestCounter;
        this.handleTextBoxChangeDebounced(event);
    };

    handleTextBoxChangeDebounced = debounce(event => {
        const searchString = event.target.value;

        if (searchString.length >= MINIMUM_STRING_SIZE || emojiRegex().test(searchString)) {
            const autocompleteRequest = this.getSearchApi(searchString);

            autocompleteRequest
                .then(response => response.json())
                .then(stories => {
                    if (this.state.fetchResponseCounter > event.fetchRequestCounter || !this.props.textBoxFocused) {
                        return;
                    }

                    // stringify all IDs from the API so we remain internally-consistent
                    stringifyIdsInAPIResponse(stories);

                    this.setState({
                        fetchResponseCounter: event.fetchRequestCounter,
                    });

                    // patients who search for doctors by name considered existing patients
                    stories.doctors.forEach(doctor => {
                        doctor.profileUrl = `${doctor.profileUrl}?isNewPatient=false`;
                    });

                    this.props.setCurrentSearchQuery(searchString);
                    this.props.setFirstSection(stories.specialties || stories.combinedSpecialtyProcedure || []);
                    this.props.setSecondSection(stories.procedures || stories.semantic || []);
                    this.props.setThirdSection(stories.doctors || []);

                    const specialtyProcedure = this.getDefaultSpecialtyProcedure();
                    const resultIsEmpty = (stories.doctors.length === 0) && (specialtyProcedure === null);

                    const resultsContent = {
                        resultIsEmpty,
                        specialtyProcedure,
                        resultIsDoctorsOnly: stories.doctors.length > 0 && specialtyProcedure === null,
                    };

                    if (this.props.monolithTrigger) {
                        this.props.monolithTrigger.trigger(AppEvents.RESULT_FETCHED, resultsContent);
                    }

                    this.props.onResultFetched(resultsContent);
                })
                .catch(err => console.log(err)); // eslint-disable-line no-console
        } else {
            this.setState({
                fetchResponseCounter: event.fetchRequestCounter,
            });
            this.props.setCurrentSearchQuery('');
            this.props.setFirstSection([]);
            this.props.setSecondSection([]);
            this.props.setThirdSection([]);
            this.props.setFocusIndex(-1);

            if (searchString.length === 0) {
                this.props.populateBrowseSpecialties();
            }
        }
    }, 100);

    calculateRelativePosition(pos) {
        const spLength = this.props.firstSection.length;

        if (pos - spLength < 0) {
            return pos + 1;
        }

        pos -= spLength;

        const prLength = this.props.secondSection.length;

        if (pos - prLength < 0) {
            return pos + 1;
        }

        return (pos - prLength) + 1;
    }

    handleTextBoxFocus = event => {
        this.props.setTextBoxFocused(true);

        if (this.props.isMobile) {
            window.scrollTo(0, 0);
            document.body.scrollTop = 0;
        }

        if (this.props.inputString.length === 0) {
            this.props.populateBrowseSpecialties();
        }

        if (!this.props.monolithTrigger) {
            this.props.sendMetric({
                action: 'Click PatientPoweredSearch TextBox',
                element: LoggingConstants.METRICS_CONSTANTS.Textbox,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.CLICK,
            });
            this.props.onTextBoxFocus(event);
        }
    };

    handleClickOutside = () => {
        this.selectMostPopularItemThenCollapseDropdown();
    };

    onKeyUp = e => {
        if (e.key === 'Escape') {
            this.selectMostPopularItemThenCollapseDropdown();
        }
    };

    sendDropdownItemSelectMetric({
        position,
        relativePosition,
        selection,
        selectionType,
    }) {
        this.props.sendMetric({
            action: 'Select PatientPoweredSearch Option',
            element: LoggingConstants.METRICS_CONSTANTS.DropdownItem,
            eventDetails: {
                Position: position,
                RelativePosition: relativePosition,
                Selection: selection,
                SelectionType: selectionType,
            },
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.SELECT,
        });
    }
}

const Container = styled.div`
    background: ${white};
    position: relative;

    ${p => p.active && css`
        ${breakpoint('xsmall', { down: true })} {
            position: fixed;
            left: 0;
            top: 0;
            right: 0;
            bottom: 0;
            z-index: 99;
        }
    `};
`;

const CancelButtonContainer = styled.div`
    align-items: center;
    display: flex;
    padding: 13px 12px;
    position: fixed;
    right: 0;
    top: 0;

    ${breakpoint.small} {
        display: none;
    }
`;

const CancelButtonDivider = styled.div`
    background-color: ${grey25};
    height: 20px;
    margin-top: 1px;
    margin-right: 11px;
    width: 1px;
`;

const CancelButton = styled.div`
    background: none;
    border: none;
    color: ${navy};
    cursor: pointer;
    font: 13px/1 ${FontFamily.SharpSansSemibold};
    margin: 0;
    padding: 0;
    text-size-adjust: none;
`;

const UniversalSearch = detectOutsideClick(withEvents(UniversalSearchBase));

const UniversalSearchPrimitives = {
    Container,
};

export {
    UniversalSearch,
    UniversalSearchPrimitives,
};
